import { Box } from '@bonusly/design-system';

import type { BizyAssistantMessageNode } from '../../../schemas/bizy-conversations';
import type { ApprovalDecision, UiBlock } from '../hooks/use-bizy-global-chat';
import { BizyStreamingMessage } from './bizy-streaming-message';
import { BizyUiBlockRenderer } from './bizy-ui-block-renderer';

interface BizyDocumentRendererProps {
	/** Ordered sequence of typed nodes — markdown content interleaved with
	 *  card blocks. Walked top-to-bottom; each node renders via its existing
	 *  legacy component (Streamdown for markdown, BizyUiBlockRenderer for cards). */
	nodes: BizyAssistantMessageNode[];
	/** Whether the streaming turn is complete. Forwarded to the markdown
	 *  renderer so the in-progress styling (italic, pulsing cursor) clears
	 *  when the document is finalized. */
	isComplete?: boolean;
	/** Stable key for the message — used by card components to record per-block
	 *  user choices (approve / draft confirm). */
	messageKey?: string;
	/** Called when content updates (for auto-scroll). */
	onContentUpdate?: () => void;
	/** Tool progress labels shown while tools are running. Forwarded only to
	 *  the active (last) markdown node — the one currently receiving tokens. */
	toolProgress?: string[];
	/** Rotating thinking phrase. Forwarded only to the active (last) markdown
	 *  node — the one currently receiving tokens. */
	thinkingPhrase?: string;
	/** Card callbacks — same contract as BizyUiBlockRenderer. */
	onConfirm?: (blockIndex: number, confirmMessage: string) => void;
	onChoiceUpdate?: (messageKey: string, blockIndex: number, choice: ApprovalDecision) => void;
	onSuggestedPageClick?: (path: string) => void;
}

/**
 * Renders a typed AssistantMessage document — Phase 1.4 of INS-725.
 *
 * Walks the node array in order and dispatches per `type`:
 * - `markdown` → BizyStreamingMessage (Streamdown markdown renderer).
 * - `approval_card` / `content_card` / `draft_card` → BizyUiBlockRenderer
 *   (the same per-card components the legacy stack already uses).
 *
 * `blockIndex` for card callbacks tracks the cumulative position of card
 * nodes across the document — so a card emitted after multiple markdown
 * sections gets a unique index for choice tracking.
 */
export function BizyDocumentRenderer({
	nodes,
	isComplete = false,
	messageKey,
	onContentUpdate,
	toolProgress,
	thinkingPhrase,
	onConfirm,
	onChoiceUpdate,
	onSuggestedPageClick,
}: BizyDocumentRendererProps) {
	let cardCount = 0;
	// Index of the last markdown node — only this one receives streaming UI
	// (tool progress / thinking phrase / pulsing cursor) when the turn is
	// in flight. Earlier markdown nodes were closed by an intervening card
	// and are finalized segments even mid-stream.
	const lastMarkdownIdx = (() => {
		for (let i = nodes.length - 1; i >= 0; i -= 1) {
			if (nodes[i].type === 'markdown') return i;
		}
		return -1;
	})();

	return (
		<Box data-testid="bizy-document-renderer">
			{nodes.map((node, idx) => {
				if (node.type === 'markdown') {
					const isActiveMarkdown = idx === lastMarkdownIdx;
					// Tolerant read: prefer the canonical `payload.content` shape but
					// fall back to a top-level `content` field if a stale sessionStorage
					// record from before INS-725 Phase 1.4 standardization survives a
					// page reload (see session-storage.ts STORAGE_KEY bump).
					const markdownContent = node.payload?.content ?? (node as { content?: string }).content ?? '';
					// Inactive markdown nodes are closed segments — render as complete
					// even when the document as a whole is still streaming, so they
					// don't show their own "Thinking..." label or pulsing cursor.
					return (
						<BizyStreamingMessage
							key={`md-${idx}`}
							content={markdownContent}
							isComplete={isComplete || !isActiveMarkdown}
							onContentUpdate={onContentUpdate}
							toolProgress={isActiveMarkdown ? toolProgress : undefined}
							thinkingPhrase={isActiveMarkdown ? thinkingPhrase : undefined}
							onSuggestedPageClick={onSuggestedPageClick}
						/>
					);
				}

				// Card node — wrap as a single-element UiBlock array for the existing
				// dispatcher. blockIndexOffset gives the card a stable index across
				// the document so onChoiceUpdate(messageKey, blockIndex, choice)
				// targets the right card on the message's uiBlocks array.
				const block: UiBlock = { type: node.type, payload: node.payload };
				const offset = cardCount;
				cardCount += 1;
				return (
					<BizyUiBlockRenderer
						key={`card-${idx}`}
						blocks={[block]}
						messageKey={messageKey}
						blockIndexOffset={offset}
						onConfirm={onConfirm}
						onChoiceUpdate={onChoiceUpdate}
					/>
				);
			})}
		</Box>
	);
}
